import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

// https://capacitorjs.com/docs/apis/geolocation

/**
 * Checks if geolocation permissions have been granted.
 * @returns {Promise<boolean>} True if location permission is granted, otherwise false.
 */
export async function checkPermissions() {
  try {
    const perms = await Geolocation.checkPermissions();
    return perms.location === 'granted';
  } catch (err) {
    console.error(err);
    return false;
  }
}

/**
 * Requests geolocation permissions from the user.
 * @returns {Promise<boolean>} A promise that resolves to a boolean indicating if permissions were granted.
 */
export async function requestPermissions() {
  try {
    const perms = await Geolocation.requestPermissions();
    return perms.location === 'granted';
  } catch (err) {
    console.error(err);
    return false;
  }
}

/**
 * Gets the current geolocation of the device.
 * @returns {Promise<Object|null>} A promise that resolves to the coordinates object of the current position or null if an error occurs.
 */
export async function getCurrentPosition() {
  try {
    // Checks if the platform is 'web' and uses the standard web API for geolocation
    if (Capacitor.getPlatform() === 'web') {
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve(position.coords),
          (err) => {
            console.error(err);
            resolve(null);
          }
        );
      });
    }
    // Uses the Capacitor Geolocation plugin for non-web platforms
    const geoloc = await Geolocation.getCurrentPosition();
    return geoloc.coords;
  } catch (err) {
    console.error(err);
  }
}

// https://en.wikipedia.org/wiki/Haversine_formula
// https://stackoverflow.com/a/1502821/4241030
export function getDistance(p1, p2) {
  const lat1 = parseFloat(p1[0]);
  const lng1 = parseFloat(p1[1]);
  const lat2 = parseFloat(p2[0]);
  const lng2 = parseFloat(p2[1]);
  const rad = (x) => (x * Math.PI) / 180;

  const R = 6378137; // Earth's mean radius in meter
  const dLat = rad(lat2 - lat1);
  const dLong = rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d; // returns the distance in meter
}
